import React, { Suspense } from 'react';

const createLazyComponent = (importFunc, componentName = 'Component') => {
  return React.lazy(() => 
    importFunc().catch(error => {
      console.error(`Failed to load ${componentName}:`, error);
      return { 
        default: () => (
          <div style={{ 
            padding: '20px', 
            textAlign: 'center', 
            color: '#666',
            border: '1px solid #ddd',
            borderRadius: '4px',
            margin: '20px'
          }}>
            <h3>Component Load Error</h3>
            <p>Failed to load {componentName}. Please refresh the page.</p>
            <button 
              onClick={() => window.location.reload()}
              style={{
                padding: '8px 16px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Refresh Page
            </button>
          </div>
        )
      };
    })
  );
};

const LoadingFallback = ({ componentName = 'Component' }) => (
  <div className="loading-container" style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '300px',
    flexDirection: 'column',
    fontFamily: 'Montserrat, sans-serif'
  }}>
    <div className="loading-spinner" style={{
      width: '40px',
      height: '40px',
      border: '3px solid #f3f3f3',
      borderTop: '3px solid #2289cb',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }}></div>
    <span style={{ marginTop: '12px', color: '#666' }}>
      Loading {componentName}...
    </span>
  </div>
);


export const LazyLogin = createLazyComponent(() => import('../Components/Home/Login'), 'Login');
export const LazyDashboard = createLazyComponent(() => import('../Components/Dashboard/Main'), 'Dashboard');

// Trip Components  
export const LazyShuttleTrips = createLazyComponent(() => import('../Components/Trips/Trips'), 'Shuttle Trips');
export const LazyPrivateTrips = createLazyComponent(() => import('../Components/PrivateDriver/PrivateTrips'), 'Private Trips');
export const LazyTrips = createLazyComponent(() => import('../Components/CommonTrips/Trips'), 'Trips');
export const LazyTripDetails = createLazyComponent(() => import('../Components/CommonTrips/TripDetails'), 'Trip Details');

// Department & Staff Components
export const LazyDepartments = createLazyComponent(() => import('../Components/Departments/Departments'), 'Departments');
export const LazyAddDepartment = createLazyComponent(() => import('../Components/Departments/AddDepartment'), 'Add Department');
export const LazyStaff = createLazyComponent(() => import('../Components/Staff/Staff'), 'Staff');
export const LazyLittleStaff = createLazyComponent(() => import('../Components/LittleStaff/Staff'), 'Little Staff');
export const LazyAdmins = createLazyComponent(() => import('../Components/Admins/Admins'), 'Admins');

// Driver & Live Tracking Components (Heavy)
export const LazyDriversData = createLazyComponent(() => import('../Components/Live Tracking/DriversData'), 'Drivers Data');
export const LazyDriverData = createLazyComponent(() => import('../Components/Live Tracking/DriversData'), 'Driver Data');
export const LazyEditDriver = createLazyComponent(() => import('../Components/PrivateDriver/Drivers/EditDriver'), 'Edit Driver');
export const LazyShuttleDrivers = createLazyComponent(() => import('../Components/Shuttle/Drivers/EditDriver'), 'Shuttle Drivers');
export const LazyDriverList = createLazyComponent(() => import('../Components/Drivers/EditDriver'), 'Driver List');
export const LazyQrCode = createLazyComponent(() => import('../Components/PrivateDriver/Drivers/QrCode'), 'QR Code');

// Route Components (Heavy with Maps)
export const LazyRoutes = createLazyComponent(() => import('../Components/Routes/Route'), 'Routes');
export const LazyStops = createLazyComponent(() => import('../Components/Routes/Stops'), 'Stops');
export const LazyTestRouteInfo = createLazyComponent(() => import('../Components/Routes/TestAddRoute/TestRouteInfo'), 'Route Info');
export const LazyStopInfo = createLazyComponent(() => import('../Components/Routes/AddRoute/StopInfo'), 'Stop Info');
export const LazyTestStopInfo = createLazyComponent(() => import('../Components/Routes/TestAddRoute/TestStopInfo'), 'Stop Info');

// Shuttle Components
export const LazyShuttleStops = createLazyComponent(() => import('../Components/Shuttle/Stops/Stops'), 'Shuttle Stops');
export const LazyBookings = createLazyComponent(() => import('../Components/Shuttle/Route Booking/Bookings'), 'Bookings');
export const LazyNewBooking = createLazyComponent(() => import('../Components/Shuttle/Route Booking/NewBooking'), 'New Booking');

// Shift Management
export const LazyShiftCreation = createLazyComponent(() => import('../Components/PrivateDriver/Create Shift/ShiftCreation'), 'Shift Creation');
export const LazyShifts = createLazyComponent(() => import('../Components/PrivateDriver/Shifts/Shifts'), 'Shifts');

// Booking Components
export const LazyBooking = createLazyComponent(() => import('../Components/ScheduleBooking/New Booking/NewBooking'), 'New Booking');
export const LazyScheduleTrips = createLazyComponent(() => import('../Components/ScheduleBooking/Trips/ScheduleTrips'), 'Schedule Trips');
export const LazyPreviousBookings = createLazyComponent(() => import('../Components/ScheduleBooking/Previous Bookings/PreviousBookings'), 'Previous Bookings');
export const LazyBookingDetails = createLazyComponent(() => import('../Components/ScheduleBooking/BookingDetails'), 'Booking Details');

// Document & Corporate Components
export const LazyDocumentsUpload = createLazyComponent(() => import('../Components/Documents Upload/DocumentsUpload'), 'Documents Upload');
export const LazyCorporateRecords = createLazyComponent(() => import('../Components/Corporates/CorporateRecord'), 'Corporate Records');
export const LazyNewRegistration = createLazyComponent(() => import('../Components/AddNewCorp/NewRegistration'), 'New Registration');

// Settings & Pricing
export const LazySettings = createLazyComponent(() => import('../Components/Settings/Settings'), 'Settings');
export const LazyAddPricingStructure = createLazyComponent(() => import('../Components/PricingStructure/AddPricingStructure'), 'Add Pricing');
export const LazyAddNewTasks = createLazyComponent(() => import('../Components/Settings/AddNewTasks'), 'Add Tasks');

// Lightweight components
export const LazySupport = createLazyComponent(() => import('../Components/Support/Support'), 'Support');

// Unauthorized component
export const LazyUnauthorized = createLazyComponent(() => import('../Components/AddNewCorp/UnauthorizedUser/Unauthorized'), 'Unauthorized');

export const preloadComponents = {
  dashboard: () => import('../Components/Dashboard/Main'),
  corporates: () => import('../Components/Corporates/CorporateRecord'),
  routes: () => import('../Components/Routes/Route'),
  staff: () => import('../Components/Staff/Staff'),
  settings: () => import('../Components/Settings/Settings'),
  support: () => import('../Components/Support/Support'),
  liveTracking: () => import('../Components/Live Tracking/DriversData'),
  trips: () => import('../Components/CommonTrips/Trips'),
};

export const bundleMonitor = {
  loadedComponents: new Set(),
  loadTimes: new Map(),
  
  trackLoad: (componentName) => {
    const startTime = performance.now();
    bundleMonitor.loadedComponents.add(componentName);
    
    return () => {
      const endTime = performance.now();
      bundleMonitor.loadTimes.set(componentName, endTime - startTime);
      console.log(`Component ${componentName} loaded in ${(endTime - startTime).toFixed(2)}ms`);
    };
  },
  
  getStats: () => ({
    totalComponents: bundleMonitor.loadedComponents.size,
    averageLoadTime: Array.from(bundleMonitor.loadTimes.values())
      .reduce((sum, time) => sum + time, 0) / bundleMonitor.loadTimes.size,
    loadedComponents: Array.from(bundleMonitor.loadedComponents),
    loadTimes: Object.fromEntries(bundleMonitor.loadTimes)
  })
};

/**
 * Route-based preloading
 * Preload components based on user navigation patterns
 */
export const routePreloader = {
  patterns: {
    '/dashboard': ['corporates', 'routes', 'liveTracking'],
    '/corporates': ['dashboard', 'staff'],
    '/routes': ['dashboard'],
    '/staff': ['corporates', 'dashboard'],
    '/settings': ['dashboard'],
    '/live-tracking': ['dashboard', 'routes']
  },
  
  preloadForRoute: (currentRoute) => {
    const componentsToPreload = routePreloader.patterns[currentRoute] || [];
    componentsToPreload.forEach(componentName => {
      if (preloadComponents[componentName]) {
        preloadComponents[componentName]().catch(err => {
          console.warn(`Failed to preload ${componentName}:`, err);
        });
      }
    });
  }
};

if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(style);
}

export { LoadingFallback };